import { DestroyRef, Injectable, inject } from "@angular/core";
import { Observable, Subject, catchError, of } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

import { CallForOpportunityService } from "../../api/services/call-for-opportunity.service";
import { CallForOpportunity } from "../../api/models/call-for-opportunity";
import { StudentProfile } from "../../api/models/student-profile";
import { ModalService } from "./modal.service";
import { AlertService, alertMessages } from "./alert.service";
import {
  ModalStudentProfileAnnouncementComponent,
  StudentAnnouncementData,
} from "../../student/student-profile/modal-student-profile-announcement/modal-student-profile-announcement.component";

/**
 * Shared "place an announcement" (call for opportunity) flow.
 *
 * Both student-top-panel-actions and the oproepen profile tab open the same
 * modal and persist via the same create endpoint; centralising the flow here
 * keeps the two call sites in sync and gives downstream code a single signal
 * to react to (the `created$` observable, used by the tab to refresh its list).
 *
 * Inject this where you need to expose a "New announcement" / "Place
 * announcement" action and subscribe to `created$` if the surface needs to
 * react to a successful create.
 *
 * @see User story #18091
 */
@Injectable({ providedIn: "root" })
export class StudentAnnouncementService {
  private readonly modal = inject(ModalService);
  private readonly alertService = inject(AlertService);
  private readonly cfoService = inject(CallForOpportunityService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly createdSubject = new Subject<CallForOpportunity>();

  /** Fires once per successful create, with the BE response payload. */
  readonly created$: Observable<CallForOpportunity> =
    this.createdSubject.asObservable();

  /**
   * Open the announcement modal for the given student profile and, on submit,
   * POST the new call for opportunity. Shows an Alert if the profile has no id
   * (matches the existing top-panel-actions guard) and surfaces BE errors via
   * AlertService.errorWithReport with a stable source tag.
   */
  open(profile: StudentProfile): void {
    if (!profile.id) {
      this.modal.Alert(
        "Profile Required",
        "You need to complete your profile before placing an announcement."
      );
      return;
    }

    const modalRef = this.modal.modalComponentRef(
      ModalStudentProfileAnnouncementComponent,
      "Place announcement",
      "",
      profile,
      [],
      { size: "lg", backdrop: "static" }
    );

    modalRef.componentInstance.announcementSubmit.subscribe(
      (data: StudentAnnouncementData) => {
        this.persist(profile, data, modalRef);
      }
    );
  }

  private persist(
    profile: StudentProfile,
    data: StudentAnnouncementData,
    modalRef: {
      close: () => void;
      componentInstance: { isSubmitting: boolean };
    }
  ): void {
    this.modal.Loader(true);

    const callForOpportunity: CallForOpportunity = {
      lookingFor: data.type,
      personalDescription: data.motivation,
      startDateTime: data.startDate,
      endDateTime: data.endDate || undefined,
      studentProfileId: profile.id!,
    };

    this.cfoService
      .createCallForOpportunity({ body: callForOpportunity })
      .pipe(
        catchError((error) => {
          this.alertService.errorWithReport(
            error,
            "studentAnnouncement:create"
          );
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((result) => {
        this.modal.Loader(false);
        if (result) {
          this.alertService.success(alertMessages.successDataSaved);
          modalRef.close();
          this.createdSubject.next(result);
        } else {
          modalRef.componentInstance.isSubmitting = false;
        }
      });
  }
}
